import React, { useState, useEffect, useRef } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { Table, Row, Col, Breadcrumb, Button, Typography, Select, Form, notification } from 'antd';
import { CSVLink } from 'react-csv';
import { reportData } from '../../redux/actions/customFunction';
import {
  intialPaginationState,
  showingPageTotalRange,
  unexpectedError,
  tailFormItemLayout,
  formItemLayout,
  PictureCell,
} from '../../common_functions';
import moment from 'moment';
const Locations = props => {
  const { roleType, prints_negatives_obj, collections_permissions, getItems } = props;
  const formRef = useRef(null);
  const csvLink = useRef(null);
  const [allData, setAllData] = useState([]);
  const [reports, setReports] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pagination, setPagination] = useState(intialPaginationState);
  const [loading, setLoading] = useState(false);
  const [collections, setCollections] = useState([]);
  const todayDateTime = moment().format('MM-DD-YYYY-HH-mm-ss');
  const columns = [
    {
      title: 'Print Object ID',
      dataIndex: 'object_id',
      key: 'object_id',
      render: text => <>{text.id}</>,
    },
    {
      title: 'Collection Name',
      dataIndex: 'object_id',
      key: 'object_id',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text.collections.name}</span>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'ID',
      dataIndex: 'acq_id',
      key: 'acq_id',
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
    },
    {
      title: 'Common Title',
      dataIndex: 'common_title',
      key: 'common_title',
    },
    {
      title: 'Thumbnail',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      render: text => (text ? <PictureCell dataIndexValue={text} getFile={props.getFile} width={50} /> : <span>-</span>),
    },
    {
      title: 'Object',
      dataIndex: 'object',
      key: 'object',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Print',
      dataIndex: 'print',
      key: 'print',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Physical Characteristics',
      dataIndex: 'phy_char',
      key: 'phy_char',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'XRF',
      dataIndex: 'xrf',
      key: 'xrf',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Micrometer',
      dataIndex: 'micrometer',
      key: 'micrometer',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Glossmeter',
      dataIndex: 'glossmeter',
      key: 'glossmeter',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Photospectrometer',
      dataIndex: 'photo_spectro_meter',
      key: 'photo_spectro_meter',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Image Description',
      dataIndex: 'image_description',
      key: 'image_description',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Mount Characteristics',
      dataIndex: 'print_mount_conditions',
      key: 'print_mount_conditions',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Basic Photos',
      dataIndex: 'basic_photos',
      key: 'basic_photos',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
  ];
  //Get collection data
  useEffect(() => {
    (async () => {
    try {
      await getCollections();
      await getLocations();
      await getColData(pagination);
    } catch (error) {
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  })();
  }, []);
  const getCollections = async () => {
    const { data: collection_data } = await getItems('collections', {
      fields: ['id', 'name'],
      sort: '-id',
      limit: -1,
      filters: {
        associated_to: {
          _in: ['print', 'both'],
        },
      },
    });
    setCollections(collection_data);
  };
  const getLocations = async (collectionID = null) => {
    let { data: locations_data } = await getItems('location', {
      fields: ['id', 'name'],
      sort: '-id',
      filters: collectionID ? { collections: { _eq: collectionID } } : {},
      limit: -1,
    });
    setLocations(locations_data);
  };
  const getColData = async (pagination, filters = {}, forCsv = false) => {
    setLoading(true);
    try {
      let location = formRef?.current?.getFieldValue('locations');
      let collections = formRef?.current?.getFieldValue('collections');
      const isAdmin = roleType === 'admin';
      const hasPrintsNegatives = prints_negatives_obj.length > 0;
      // Common filter structure
      const commonFilters = location
        ? { object_id: { location: { id: { _eq: location } } } }
        : { object_id: { location: { id: { _neq: null } } } };

      let collectionFilter = collections && isAdmin ? { collections: { _eq: collections } } : {};
      // Apply filters based on conditions
      if (!isAdmin && hasPrintsNegatives) {
        collectionFilter = collections
          ? { _and: [{ _in: collections_permissions.view }, { _eq: collections }] }
          : { collections: { _in: collections_permissions.view } };
        filters['_and'] = [{ object_id: { _in: prints_negatives_obj } }, commonFilters];
      } else if (isAdmin || hasPrintsNegatives) {
        filters;
        filters = {
          ...filters,
          ...collectionFilter,
          ...commonFilters,
        };
      }
      const dataFilters = {
        fields: ['*,object_id.id,object_id.collections.name'],
        sort: '-id',
        filters: filters,
        ...(forCsv
          ? { limit: '-1' }
          : {
              offset: pagination.pageSize * (pagination.current - 1),
              limit: pagination.pageSize,
              meta: 'filter_count',
            }),
      };
      const response = await getItems('basic_print_reports', dataFilters);
      setReports(response.data);
      if (!forCsv) {
        pagination.total = response.meta.filter_count;
        setPagination(pagination);
      } else {
        setAllData(reportData(response.data));
      }
    } catch (error) {
      console.error(error);
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };
  const handleTableChange = async pagination => {
    setPagination(pagination);
    await getColData(pagination, {}, false);
  };
  const onFinish = async () => {
    setReports([]);
    setPagination(intialPaginationState);
    await getColData(intialPaginationState);
  };
  const getCollectionByLocation = async location => {
    setReports([]);
    setPagination(intialPaginationState);
    await getColData(intialPaginationState);
  };
  const getCSVData = async () => {
    await getColData(intialPaginationState, {}, true);
  };
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (allData.length > 0) {
      csvLink.current.link.click();
    } else {
      notification.info({
        message: 'No data available for download',
        duration: 2,
      });
    }
  }, [allData]);
  return (
    <Row className={!!loading ? 'disabledComponent' : ''}>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Reports", href: "/reports/reading-reports/locations" }, { title: "Location Report" }]} />
      </Col>
      <Col className="white-paper" span={24}>
        <div className="dashboard">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Reports
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end" style={{ display: 'flex' }}>
              <Button icon={<DownloadOutlined />} className="align-right" type="primary" size="small" onClick={getCSVData}>
                Download Csv
              </Button>
              <CSVLink
                ref={csvLink}
                data={allData}
                filename={`location_report_${todayDateTime}.csv`}
                onClick={() => {}}
                style={{ display: 'none' }}
              ></CSVLink>
            </Col>
          </Row>
          <Form ref={formRef} layout="vertical" onFinish={onFinish} {...formItemLayout} initialValues={{ layout: tailFormItemLayout }}>
            <Row gutter={24}>
              <Col xs={24} sm={8} md={8} lg={8}>
                <Form.Item name="collections" label="Collection">
                  <Select
                    placeholder="Select collection"
                    optionFilterProp="children"
                    allowClear
                    filterOption={(input, option) => option.children.includes(input)}
                    onChange={getLocations}
                  >
                    {collections.map(col => {
                      return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={8} lg={8}>
                <Form.Item name="locations" label="Locations">
                  <Select
                    placeholder="Select Location"
                    optionFilterProp="children"
                    showSearch
                    allowClear
                    filterOption={(input, option) => option.children.includes(input)}
                    // onChange={getCollectionByLocation}
                  >
                    {locations.map(col => {
                      return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={4} md={4} lg={4}>
                <Form.Item>
                  <Button type="primary" size="small" htmlType="submit" style={{ height: '42px', marginTop: '28px' }}>
                    Search
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <hr />
          <div className="artist-list">
            <Table
              className="no-fixed-last-col"
              scroll={{ x: 'max-content' }}
              columns={columns}
              bordered
              rowKey={record => record.id}
              dataSource={reports}
              pagination={reports.length > 0 ? { ...pagination, ...showingPageTotalRange } : false}
              loading={loading}
              onChange={handleTableChange}
            />
          </div>
        </div>
      </Col>
    </Row>
  );
};
export default Locations;
